import dayjs from 'dayjs';
import { amplitudeLib, FlowType } from 'freedom-shared';

import { SearchMode } from '@/shared/config';

import type { extractAnalyticsDataTypes } from '../analytics-data-extraction';

/**
 * Derives the canonical `SearchMode` from the parsed airflow query object
 * (`airflow.utils.parseQuery(...)`). Returns `undefined` when the query
 * could not be parsed.
 */
export const getSearchModeFromQuery = (
  searchParams: { OW?: boolean; RT?: boolean; ML?: boolean } | null | undefined,
): SearchMode | undefined => {
  if (!searchParams) {
    return undefined;
  }
  if (searchParams.ML) {
    return SearchMode.MULTI_TRIP;
  }
  if (searchParams.RT) {
    return SearchMode.ROUND_TRIP;
  }
  if (searchParams.OW) {
    return SearchMode.ONE_WAY;
  }
  return undefined;
};

export type MainPageVariant = 'old_variant' | 'new_variant';

/**
 * Returns the canonical main-page variant for analytics tagging.
 *
 * The `avfree_main_page_redesign` A/B test concluded with `new_variant` rolled
 * out to 100%, so this is now a constant. Kept as a function so funnel-event
 * helpers can keep auto-tagging `main_page_variant` without further changes.
 */
export const getMainPageVariant = (): MainPageVariant => {
  return 'new_variant';
};

let isMainPageOpenEventSent = false;

type InfoForGetRedirectionSource = {
  isFirstTime: boolean;
  source: string | null;
};

export const getRedirectionSource = (info: InfoForGetRedirectionSource) => {
  if (info.source === 'tabbar') {
    return 'tabbar';
  }
  let handledSource = 'flow';
  if (info.isFirstTime) {
    if (info.source) {
      handledSource = info.source;
    } else {
      handledSource = 'native';
    }
  }
  return handledSource;
};

export const mainPageOpen = (
  source: string | null,
  variant?: MainPageVariant,
) => {
  const redirectionSource = getRedirectionSource({
    isFirstTime: !isMainPageOpenEventSent,
    source,
  });

  const properties = {
    source: redirectionSource,
    app: FlowType.AVIA,
    main_page_variant: variant,
  };
  amplitudeLib.logEvent('free_main_page', properties);
  isMainPageOpenEventSent = true;
};

export const submitSearchForm = (
  params: extractAnalyticsDataTypes.ExtractSearchFormDataParams,
  source: string,
  variant?: MainPageVariant,
) => {
  const bookingHorizon = params.date_from
    ? dayjs(params.date_from).diff(dayjs().startOf('day'), 'day')
    : undefined;

  const mappedSearchFormParams = {
    ...params,
    city_from: params.city_from.code,
    city_to: params.city_to.code,
    source,
  };
  amplitudeLib.logEvent('free_search_button_click', {
    ...mappedSearchFormParams,
    booking_horizon: bookingHorizon,
    search_mode: params.search_mode,
    app: FlowType.AVIA,
    main_page_variant: variant,
  });
};

export const switchCitiesClick = () => {
  amplitudeLib.logEvent('avfree_switch_cities_click');
};

export const blogClick = () => {
  amplitudeLib.logEvent('avfree_blog_page_click');
};

export const ticketPageClick = (sourceScreen: string | null) => {
  amplitudeLib.logEvent('avfree_ticket_page_click', {
    source: sourceScreen,
  });
};

export const mainScroll = (params: {
  scroll_depth: number;
  scroll_blocks: string[];
}) => {
  amplitudeLib.logEvent('avree_main_scroll', params);
};

export type MultiTripToggleDirection = 'enter_mt' | 'exit_mt';

export const multiTripToggleClick = (params: {
  direction: MultiTripToggleDirection;
  source: string;
}) => {
  amplitudeLib.logEvent('avfree_mt_toggle_click', {
    direction: params.direction,
    source: params.source,
  });
};

export const multiTripAutoExit = (params: { source: string }) => {
  amplitudeLib.logEvent('avfree_mt_auto_exit', {
    source: params.source,
  });
};

export const multiTripInfoOpen = (params: {
  source: string;
  legs_count: number;
}) => {
  amplitudeLib.logEvent('avfree_mt_info_open', {
    source: params.source,
    legs_count: params.legs_count,
  });
};

export type MultiTripValidationErrorCode =
  | 'requiredDep'
  | 'requiredArr'
  | 'isSameDeparture'
  | 'isSameArrival'
  | 'requiredDate'
  | 'dateBeforePrev'
  | 'unknown';

export const multiTripSubmitValidationFailed = (params: {
  error_codes: MultiTripValidationErrorCode[];
  legs_count: number;
  failed_leg_indexes: number[];
  source: string;
}) => {
  amplitudeLib.logEvent('avfree_mt_submit_validation_failed', {
    error_codes: params.error_codes,
    legs_count: params.legs_count,
    failed_leg_indexes: params.failed_leg_indexes,
    source: params.source,
  });
};

export type AiChatOpenSource =
  | 'entry_card'
  | 'entry_action'
  | 'book_offer'
  | 'auto_open';

export const aiChatOpen = (source: AiChatOpenSource = 'entry_card') => {
  amplitudeLib.logEvent('avfree_ai_chat_open', { source });
};

export const mainPageLayoutAssigned = (widgets: readonly string[]) => {
  amplitudeLib.logEvent('avfree_main_page_layout_assigned', {
    main_page_layout: widgets,
  });
};
